import React, { useCallback, useEffect } from 'react';
import { ServerPaginatedTable } from 'components/table';
import { columnType } from 'components/table/types';
import _ from 'lodash';
import { useAppDispatch, useAppSelector } from 'hooks/redux.hooks';
import { selectTranslations } from 'state/reducers/translation.reducers';
import { getCompaniesWithExpendedData, getCompaniesExpendedPagination } from 'state/reducers/global.reducer';
import { useNavigate } from 'react-router-dom';
import { pagesEnum } from 'constants/ui.constants';
import { CustomCompanySwitch, RiskLevel } from './elements';
import { getAllCompaniesExpendedData } from 'services/companies.service';
import { Order } from 'components/table/types';
import { DEFAULT_PAGE_SIZE, DEFAULT_SORT_ORDER } from 'constants/pagination.constants';
import { useServerPagination } from 'hooks/pagination/useServerPagination';

export const tableColumns = {
	hyper_account_number: 'string',
	created_at: 'date',
	company_identity_number: 'number',
	hebrew_name: 'string',
	english_name: 'string',
	risk_level: 'custom',
	closedByBoUserId: 'custom',
};

const INITIAL_SORT_BY = 'created_at';
const INITIAL_SORT_ORDER: Order = DEFAULT_SORT_ORDER;

const AccountsTable: React.FC = () => {
	const tr = useAppSelector(selectTranslations);
	const dispatch = useAppDispatch();
	const companies = useAppSelector(getCompaniesWithExpendedData);
	const pagination = useAppSelector(getCompaniesExpendedPagination);
	const navigate = useNavigate();
	const mapSortBy = (sortBy: string) => sortBy || INITIAL_SORT_BY;

	const fetchCompanies = useCallback(
		async (page: number, sortOverride: { sortBy: string; sortOrder: Order }) => {
			await dispatch(
				getAllCompaniesExpendedData(
					{
						offset: page * DEFAULT_PAGE_SIZE,
						limit: DEFAULT_PAGE_SIZE,
						sortBy: mapSortBy(sortOverride.sortBy),
						sortOrder: sortOverride.sortOrder,
					},
					true
				)
			);
		},
		[dispatch]
	);

	const { currentPage, isLoading, runFetch, handlePageChange, handleSortChange } = useServerPagination({
		initialSortBy: INITIAL_SORT_BY,
		initialSortOrder: INITIAL_SORT_ORDER,
		fetchPage: fetchCompanies,
	});

	useEffect(() => {
		runFetch(0);
	}, [runFetch]);

	const handleSortChangeWithState = useCallback(
		(nextSortBy: string, nextSortOrder: Order) => handleSortChange(nextSortBy, nextSortOrder),
		[handleSortChange]
	);

	const buildColumns = (): columnType[] => {
		const columns: columnType[] = [];
		_.keys(tableColumns).forEach((element: any) => {
			let customValueHandle = {};
			if (element === 'closedByBoUserId') {
				customValueHandle = {
					customValueHandle: (value: any) => {
						return <CustomCompanySwitch closed={Boolean(value)} />;
					},
				};
			}
			if (element === 'risk_level') {
				customValueHandle = {
					customValueHandle: (value: any) => {
						return <RiskLevel riskLevel={value} noLabel={true} />;
					},
				};
			}

			const props: columnType = {
				fieldId: 'id',
				name: element,
				title: tr('page.customerAccounts.tables.accounts.field.' + _.camelCase(element)),
				type: _.get(tableColumns, element),
				sortable: true,
				...customValueHandle,
			};
			columns.push(props);
		});
		return columns;
	};

	return (
		<ServerPaginatedTable
			rowAction={(row) => {
				navigate(`/${pagesEnum.CUSTOMER_ACCOUNTS}/${row.id}`);
			}}
			defaultSortBy={INITIAL_SORT_BY}
			defaultOrderBy={INITIAL_SORT_ORDER}
			noShrink
			data={companies || []}
			columns={buildColumns()}
			perPage={DEFAULT_PAGE_SIZE}
			serverPagination={{
				total: pagination.total,
				hasMore: pagination.hasMore,
				currentPage,
				onPageChange: handlePageChange,
				onSortChange: handleSortChangeWithState,
				isLoading,
			}}
		/>
	);
};

export default AccountsTable;
